"use client"
import {
  type UseQueryResult,
  useQueries,
  useQueryClient,
} from "@tanstack/react-query"
import type { Omit } from "@wagmi/core/internal"
import { useCallback, useMemo } from "react"
import { Address, Evaluate } from "viem"
import { useChainId, useConfig } from "wagmi"
import { type GetPriceErrorType, getPrice } from "../actions/getPrice"
import {
  type GetPriceData,
  type GetPriceOptions,
  type GetPriceQueryFnData,
  type GetPriceQueryKey,
  getPriceQueryKey,
} from "../query"
import type {
  ConfigParameter,
  ExtendedConfig,
  UseQueryParameters,
} from "../types"

export type UsePricesProps<
  config extends ExtendedConfig = ExtendedConfig,
  selectedData = GetPriceData,
  combineData = UseQueryResult<selectedData>[],
> = ConfigParameter<config> & {
  queries: Evaluate<
    GetPriceOptions &
      ConfigParameter<config> & {
        query?:
          | Evaluate<
              Omit<
                UseQueryParameters<
                  GetPriceQueryFnData,
                  GetPriceErrorType,
                  selectedData,
                  GetPriceQueryKey
                >,
                "gcTime" | "staleTime"
              >
            >
          | undefined
      }
  >[]
  select?: (data: GetPriceData) => selectedData
  combine?: (result: UseQueryResult<selectedData>[]) => combineData
}

export function usePrices<
  config extends ExtendedConfig,
  selectedData = GetPriceData,
  combineData = UseQueryResult<selectedData>[],
>(parameters: UsePricesProps<config, selectedData, combineData>): combineData {
  const { queries = [], select, combine } = parameters

  const config = useConfig(parameters)
  const chainId = useChainId({ config })
  const queryResults = useQueries({
    queries: queries.map(({ query = {}, ...options }) => ({
      ...query,
      queryKey: getPriceQueryKey({
        ...options,
        chainId: options.chainId || chainId,
      }),
      queryFn: () =>
        getPrice(config, {
          ...options,
          chainId: options.chainId || chainId,
        }),
      select: select,
      enabled: Boolean(options.token && (query.enabled ?? true)),
      refetchOnMount: false,
      retry: false,
    })),
  }) satisfies UseQueryResult<selectedData>[]

  return useMemo(
    () =>
      combine
        ? combine(queryResults)
        : (queryResults as unknown as combineData),
    [queryResults, combine],
  )
}

export function useInvalidatePricesQueryKeys() {
  const queryClient = useQueryClient()
  const chainId = useChainId()

  return useCallback(
    (tokens?: Address[]) => {
      if (tokens && tokens.length > 0) {
        // Invalidate specific unit of account queries
        tokens.forEach((token) => {
          const queryKey = getPriceQueryKey({
            token,
            chainId,
          })
          queryClient.invalidateQueries({ queryKey })
        })
      } else {
        // Invalidate all unit of account price queries
        queryClient.invalidateQueries({
          predicate: (query) => query.queryKey[0] === "price",
        })
      }
    },
    [queryClient, chainId],
  )
}
